import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { formatSar } from '@wasity/shared';
import { Bar, Button, Card, Chip, ErrorNotice, Skeleton, Sparkline, Stat, Table, Timeline } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import type { Me } from '@/lib/auth';
import type { Analytics, BR, Queue } from '@/lib/types';
import { AdminBrChip, AdminEvChip, dailySeries, settingValue, useSettings } from './common';

export function AdminDashboard({ me }: { me: Me }) {
  const { t, locale, localTitle } = useI18n();
  const queue = useQuery({ queryKey: ['admin', 'queue'], queryFn: () => api.get<Queue>('/api/admin/queue'), refetchInterval: 30_000 });
  const analytics = useQuery({ queryKey: ['admin', 'analytics'], queryFn: () => api.get<Analytics>('/api/admin/analytics') });
  const latest = useQuery({ queryKey: ['admin', 'br', 'latest'], queryFn: () => api.get<{ items: BR[] }>('/api/admin/broker-requests?limit=6') });
  const settings = useSettings();
  if (queue.isLoading || analytics.isLoading) return <Skeleton lines={8} />;
  if (queue.error || analytics.error) return <ErrorNotice error={queue.error ?? analytics.error} retry={() => void queue.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const q = queue.data!;
  const a = analytics.data!;
  const windowMin = settingValue<number>(settings.data?.items, 'accept_window.minutes', 60);
  const queueCount = q.brokerRequests.filter((r) => r.status === 'new').length + q.evaluations.filter((e) => e.status === 'new').length;
  const returned = [...q.brokerRequests.filter((r) => r.status === 'new' && r.returnedAt), ...q.evaluations.filter((e) => e.status === 'new' && e.returnedAt)];
  const attention = [
    ...returned.map((r) => ({ id: `ret-${r.id}`, at: r.returnedAt!, tone: 'warning' as const, title: t('dashboard.expiredLine', { number: r.number }), body: <span>{t('dashboard.expiredBody', { title: localTitle(r) })} <Link to={`/admin/queue?select=${r.id}`}>{t('dashboard.reassign')}</Link></span> })),
    ...q.commissionsDue.map((r) => ({ id: `com-${r.id}`, at: r.completedAt ?? r.updatedAt, tone: 'warning' as const, title: t('dashboard.commissionLine', { number: r.number }), body: <span>{t('dashboard.commissionBody', { amount: money(r.commissionHalalas, locale), broker: r.brokerName ?? '' })} <Link to={`/admin/broker-requests/${r.id}`}>{t('dashboard.confirmReceipt')}</Link></span> })),
    ...q.refundsNeeded.map((e) => ({ id: `ref-${e.id}`, at: e.refundNeededAt!, tone: 'warning' as const, title: t('dashboard.refundLine', { number: e.number }), body: <span>{t('dashboard.refundBody', { amount: money(e.amountHalalas, locale) })} <Link to={`/admin/evaluations/${e.id}`}>{t('app.open')}</Link></span> })),
    ...(q.signupsPending ? [{ id: 'signups', at: new Date(), tone: undefined, title: t('dashboard.signupsLine', { n: q.signupsPending }), body: <Link to="/admin/signups">{t('dashboard.review')}</Link> }] : []),
    ...q.membershipsPastDue.map((m) => ({ id: `pd-${m.userId}`, at: m.periodEnd ?? new Date(), tone: 'muted' as const, title: t('dashboard.pastDueLine'), body: <Link to={`/admin/users/${m.userId}`}>{t('dashboard.pastDueBody', { name: m.name })}</Link> })),
  ];
  const brTrend = dailySeries(a.brokerRequests.trend, 30);
  const evTrend = dailySeries(a.evaluations.trend, 30);
  const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);
  const today = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { dateStyle: 'full', timeZone: 'Asia/Riyadh' }).format(new Date());
  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[13px] text-muted mb-2">{today}</p>
          <h1>{t('dashboard.goodMorning', { name: me.user.name.split(' ')[0] ?? '' })}</h1>
        </div>
        <div className="flex gap-2.5">
          <Link to="/admin/analytics">
            <Button variant="secondary" className="min-h-9 text-[14px]">{t('nav.analytics')}</Button>
          </Link>
          <Link to="/admin/queue">
            <Button variant="primary" className="min-h-9 text-[14px]">
              {t('nav.queue')} · <span className="num">{queueCount}</span>
            </Button>
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t('nav.queue')} value={queueCount} tone={queueCount ? 'warning' : undefined} hint={`${t('dashboard.brokersShort', { n: q.brokerRequests.filter((r) => r.status === 'new').length })} · ${t('dashboard.evaluationsShort', { n: q.evaluations.filter((e) => e.status === 'new').length })}`} />
        <Stat label={t('dashboard.commissionsDue')} value={formatSar(a.commissionsDue.halalas, locale)} hint={t('dashboard.completedN', { n: a.commissionsDue.n })} />
        <Stat label={t('dashboard.activeMembers')} value={a.members.brokersActive + a.members.evaluatorsActive} hint={`${t('dashboard.brokersN', { n: a.members.brokersActive })} · ${t('dashboard.evaluatorsN', { n: a.members.evaluatorsActive })}`} />
        <Stat label={t('dashboard.listingsOnMap')} value={a.listingsActive} hint={`${t('admin.analytics.users')}: ${a.members.users}`} />
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <Card>
          <h2 className="mb-4">{t('dashboard.needsAttention')}</h2>
          {attention.length ? <Timeline items={attention} /> : <p className="text-muted text-[14px]">{t('dashboard.allClear')}</p>}
        </Card>
        <div className="flex flex-col gap-3">
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h2>{t('dashboard.requestsTrend')}</h2>
              <span className="flex gap-4 text-[13px] text-muted">
                <span><i className="inline-block size-2.5 rounded-sm bg-accent me-1.5 align-middle" />{t('nav.brokers')}</span>
                <span><i className="inline-block size-2.5 rounded-sm bg-accent-2 me-1.5 align-middle" />{t('nav.evaluations')}</span>
              </span>
            </div>
            <Sparkline series={[{ points: brTrend, area: true }, { points: evTrend, color: 'var(--accent-2)' }]} />
            <p className="text-[13px] num text-center mt-1"><b>{sum(brTrend)}</b> · <b>{sum(evTrend)}</b></p>
          </Card>
          <Card>
            <h2 className="mb-2">{t('dashboard.avgAcceptance')}</h2>
            <div className="flex items-center justify-between">
              <span className="text-[28px] font-light num">{t('admin.analytics.minutesN', { n: a.avgAcceptanceMinutes })}</span>
              <Chip form="mint">{t('dashboard.windowIs', { n: windowMin })}</Chip>
            </div>
            <Bar value={a.avgAcceptanceMinutes} max={windowMin} className="mt-3" />
          </Card>
        </div>
      </div>
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2>{t('dashboard.latest')}</h2>
          <Link to="/admin/broker-requests">{t('app.viewAll')}</Link>
        </div>
        {latest.data?.items.length ? (
          <Table>
            <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.owner')}</th><th>{t('app.broker')}</th><th>{t('app.status')}</th><th>{t('app.updated')}</th></tr></thead>
            <tbody>
              {latest.data.items.map((r) => (
                <tr key={r.id}>
                  <td className="num"><Link to={`/admin/broker-requests/${r.id}`}>{r.number}</Link></td>
                  <td>{localTitle(r)}</td>
                  <td>{r.ownerName ?? '—'}</td>
                  <td className={r.brokerName ? '' : 'text-muted'}>{r.brokerName ?? '—'}</td>
                  <td><AdminBrChip br={r} /></td>
                  <td className="text-muted text-[13px] whitespace-nowrap">{formatRelative(r.updatedAt, locale)}</td>
                </tr>
              ))}
              {q.evaluations.slice(0, 2).map((e) => (
                <tr key={e.id}>
                  <td className="num"><Link to={`/admin/evaluations/${e.id}`}>{e.number}</Link></td>
                  <td>{localTitle(e)}</td>
                  <td>{e.ownerName ?? '—'}</td>
                  <td className={e.evaluatorName ? '' : 'text-muted'}>{e.evaluatorName ?? '—'}</td>
                  <td><AdminEvChip ev={e} /></td>
                  <td className="text-muted text-[13px] whitespace-nowrap">{formatRelative(e.updatedAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted text-[14px]">{t('app.none')}</p>
        )}
      </Card>
    </div>
  );
}
